import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '@/src/lib/supabase';
import { Button } from '@/src/components/ui/button';
import {
  MessageSquare,
  Copy,
  Check,
  ExternalLink,
  Clock,
  FileText,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Send
} from 'lucide-react';

export interface BookingMessageItem {
  key: string;
  label: string;
  phone: string;
  message: string;
  enabled: boolean;
  reason?: string | null;
}

export interface BookingEventItem {
  event: string;
  from_status?: string | null;
  to_status?: string | null;
  via?: string | null;
  note?: string | null;
  at: string;
}

interface ServiceBookingMessagesPanelProps {
  bookingId: string;
  onRefresh?: () => void;
}

export function ServiceBookingMessagesPanel({
  bookingId,
  onRefresh
}: ServiceBookingMessagesPanelProps) {
  const supabase = getSupabaseClient();

  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<BookingMessageItem[]>([]);
  const [events, setEvents] = useState<BookingEventItem[]>([]);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Invoice bill share state
  const [billShare, setBillShare] = useState<{
    phone?: string;
    message?: string;
    invoice_number?: string;
  } | null>(null);
  const [billError, setBillError] = useState<string | null>(null);

  const fetchMessagesAndEvents = useCallback(async () => {
    if (!supabase || !bookingId) return;
    setLoading(true);
    try {
      // 1. Fetch messages
      const { data: msgData, error: msgErr } = await supabase.rpc('get_service_booking_messages', {
        p_booking_id: bookingId
      });
      if (msgErr) {
        console.warn('get_service_booking_messages error:', msgErr);
      } else if (msgData) {
        setMessages(msgData.messages || []);
      }

      // 2. Fetch timeline events
      const { data: evData, error: evErr } = await supabase.rpc('get_booking_events', {
        p_booking_id: bookingId
      });
      if (evErr) {
        console.warn('get_booking_events error:', evErr);
      } else {
        setEvents(evData || []);
      }

      // 3. Check invoice share
      try {
        const { data: invData, error: invErr } = await supabase.rpc('get_service_invoice_share', {
          p_booking_id: bookingId
        });
        if (invErr) {
          setBillError(invErr.message || 'Invoice not generated yet');
          setBillShare(null);
        } else if (invData) {
          setBillShare(invData);
          setBillError(null);
        }
      } catch {
        setBillError('Invoice not generated yet');
      }
    } catch (err: any) {
      console.error('fetchMessagesAndEvents error:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase, bookingId]);

  useEffect(() => {
    fetchMessagesAndEvents();
  }, [fetchMessagesAndEvents]);

  // Log message event helper
  const handleLogEvent = async (msgKey: string, noteText: string) => {
    if (!supabase || !bookingId) return;
    const eventName = `msg_${msgKey}`;
    try {
      await supabase.rpc('log_service_booking_event', {
        p_booking_id: bookingId,
        p_event: eventName,
        p_note: noteText
      });
      // Refresh events timeline
      const { data: evData } = await supabase.rpc('get_booking_events', {
        p_booking_id: bookingId
      });
      if (evData) setEvents(evData);
      if (onRefresh) onRefresh();
    } catch (logErr) {
      console.warn('log_service_booking_event warning:', logErr);
    }
  };

  // Open WhatsApp in new tab and log event
  const handleSendWhatsApp = (item: BookingMessageItem) => {
    if (!item.phone || !item.message) return;
    const cleanPhone = item.phone.replace(/\D/g, '');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(item.message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleLogEvent(item.key, `Sent on WhatsApp (${item.label})`);
    toast.success(`WhatsApp opened for ${item.label}`);
  };

  // Copy message to clipboard and log event
  const handleCopyMessage = (item: BookingMessageItem) => {
    navigator.clipboard.writeText(item.message);
    setCopiedKey(item.key);
    toast.success(`Copied ${item.label} message`);
    handleLogEvent(item.key, `Copied to clipboard (${item.label})`);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  // Send Invoice Bill on WhatsApp
  const handleSendBillWhatsApp = () => {
    if (!billShare?.phone || !billShare?.message) return;
    const cleanPhone = billShare.phone.replace(/\D/g, '');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(billShare.message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleLogEvent('bill', `Sent final bill on WhatsApp (Inv #${billShare.invoice_number})`);
    toast.success('WhatsApp opened with final invoice bill');
  };

  const handleCopyBill = () => {
    if (!billShare?.message) return;
    navigator.clipboard.writeText(billShare.message);
    setCopiedKey('bill');
    toast.success('Copied final bill message');
    handleLogEvent('bill', 'Copied final bill message');
    setTimeout(() => setCopiedKey(null), 2500);
  };

  return (
    <div className="space-y-6">
      {/* SECTION 1: Customer messages (WhatsApp) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
            <MessageSquare size={14} className="text-emerald-600 dark:text-emerald-400" />
            <span>Customer Messages (WhatsApp)</span>
          </h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchMessagesAndEvents}
            disabled={loading}
            className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-900 dark:hover:text-white"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin mr-1' : 'mr-1'} />
            <span>Refresh</span>
          </Button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-400 text-xs flex flex-col items-center gap-1.5 border border-slate-200 dark:border-slate-800 rounded-xl">
            <RefreshCw size={16} className="animate-spin text-primary" />
            <span>Loading messages & timeline...</span>
          </div>
        ) : (
          <div className="space-y-2.5">
            {messages.map(item => (
              <div
                key={item.key}
                className={`p-3 rounded-xl border transition-all text-xs space-y-2 ${
                  item.enabled
                    ? 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xs'
                    : 'border-slate-200/60 dark:border-slate-800/60 bg-slate-50/70 dark:bg-slate-800/30 opacity-75'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-slate-900 dark:text-white">
                      {item.label}
                    </span>
                    {item.enabled ? (
                      <span className="inline-flex items-center px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        Ready to send
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.2 rounded-full text-[9px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        {item.reason || 'Disabled'}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyMessage(item)}
                      disabled={!item.enabled}
                      className="h-7 px-2 text-[11px] border-slate-200 dark:border-slate-700 gap-1 text-slate-600 dark:text-slate-300"
                    >
                      {copiedKey === item.key ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                      <span>Copy</span>
                    </Button>

                    <Button
                      size="sm"
                      onClick={() => handleSendWhatsApp(item)}
                      disabled={!item.enabled}
                      className="h-7 px-2.5 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shadow-2xs disabled:opacity-40"
                    >
                      <Send size={11} />
                      <span>Send on WhatsApp</span>
                    </Button>
                  </div>
                </div>

                {/* Message Text Preview */}
                <div className="p-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-100 dark:border-slate-800 text-[11px] text-slate-700 dark:text-slate-300 font-sans leading-relaxed whitespace-pre-wrap">
                  {item.message}
                </div>
              </div>
            ))}

            {/* Final Bill Row */}
            <div
              className={`p-3 rounded-xl border transition-all text-xs space-y-2 ${
                billShare
                  ? 'border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-indigo-950/20 shadow-2xs'
                  : 'border-slate-200/60 dark:border-slate-800/60 bg-slate-50/70 dark:bg-slate-800/30 opacity-75'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <FileText size={14} className="text-indigo-600 dark:text-indigo-400" />
                  <span className="font-bold text-slate-900 dark:text-white">
                    Final Bill / Invoice
                  </span>
                  {billShare ? (
                    <span className="inline-flex items-center px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                      Invoice #{billShare.invoice_number}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-1.5 py-0.2 rounded-full text-[9px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                      {billError || 'Invoice not generated yet'}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyBill}
                    disabled={!billShare}
                    className="h-7 px-2 text-[11px] border-slate-200 dark:border-slate-700 gap-1 text-slate-600 dark:text-slate-300"
                  >
                    {copiedKey === 'bill' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                    <span>Copy</span>
                  </Button>

                  <Button
                    size="sm"
                    onClick={handleSendBillWhatsApp}
                    disabled={!billShare}
                    className="h-7 px-2.5 text-[11px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white gap-1 shadow-2xs disabled:opacity-40"
                  >
                    <Send size={11} />
                    <span>Send Bill on WhatsApp</span>
                  </Button>
                </div>
              </div>

              {billShare?.message ? (
                <div className="p-2.5 bg-white dark:bg-slate-900 rounded-lg border border-indigo-100 dark:border-slate-800 text-[11px] text-slate-700 dark:text-slate-300 font-sans leading-relaxed whitespace-pre-wrap">
                  {billShare.message}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 italic">
                  Complete the service and generate the final bill to share the invoice link with the customer.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* SECTION 2: Timeline (from get_booking_events) */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
          <Clock size={14} className="text-primary" />
          <span>Timeline & Audit Trail ({events.length})</span>
        </h4>

        {events.length === 0 ? (
          <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/40 text-center text-xs text-slate-400">
            No events logged yet for this booking.
          </div>
        ) : (
          <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-2.5">
            {events.map((ev, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2.5 text-xs pb-2 border-b last:border-b-0 last:pb-0 border-slate-100 dark:border-slate-800"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                <div className="flex-1 space-y-0.5">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900 dark:text-white capitalize">
                      {ev.from_status && ev.to_status
                        ? `${ev.from_status} → ${ev.to_status}`
                        : ev.event?.replace('_', ' ')}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {new Date(ev.at).toLocaleString()}
                    </span>
                  </div>

                  {ev.note && (
                    <p className="text-[11px] text-slate-600 dark:text-slate-300">
                      {ev.note}
                    </p>
                  )}

                  {ev.via && (
                    <span className="inline-block text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">
                      via: {ev.via}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ServiceBookingMessagesPanel;
